import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { House, Buildings, ChartBar, EnvelopeSimple, TrendUp, CurrencyEur, ArrowSquareOut, Spinner } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#4f46e5'];

const TYPE_LABELS = {
    apartment: 'properties.apartment',
    house: 'properties.house',
    land: 'properties.land',
    office: 'properties.office',
    retail: 'properties.retail',
    warehouse: 'properties.warehouse',
    other: 'properties.other',
    investment: 'properties.investment'
};

function formatCurrency(value) {
    if (!value) return '0 €';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} Mio. €`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}.000 €`.replace('.000.000', ' Mio.');
    return `${value.toLocaleString('de-DE')} €`;
}

function StatCard({ icon: Icon, title, value, subtitle, color = 'text-primary' }) {
    return (
        <Card className="border">
            <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-2xl font-bold mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                    <div className={`p-2 rounded-sm bg-muted ${color}`}>
                        <Icon size={20} weight="fill" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function AnalyticsPage() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: analytics } = await axios.get(`${API}/api/analytics/overview`, { withCredentials: true });
                setData(analytics);
            } catch (error) {
                console.error('Error fetching analytics:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
                <Spinner className="animate-spin mr-2" size={24} />
                <span className="text-muted-foreground">{t('common.loading')}</span>
            </div>
        );
    }

    if (!data) return null;

    const typeChartData = Object.entries(data.properties.by_type || {}).map(([key, count]) => ({
        name: t(TYPE_LABELS[key] || `properties.${key}`),
        value: count
    }));

    const marketingChartData = Object.entries(data.properties.by_marketing || {}).map(([key, count]) => ({
        name: key === 'sale' ? t('analytics.sale') : t('analytics.rent'),
        value: count
    }));

    const cityChartData = data.properties.by_city || [];

    const inquiryChartData = data.inquiries.by_day || [];

    return (
        <div className="space-y-6" data-testid="analytics-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    {t('analytics.title')}
                </h1>
                <p className="text-muted-foreground mt-1">{t('analytics.subtitle')}</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
                <StatCard
                    icon={House}
                    title={t('analytics.totalProperties')}
                    value={data.properties.total}
                    subtitle={`${data.properties.active} ${t('common.active')}`}
                    color="text-blue-600"
                />
                <StatCard
                    icon={CurrencyEur}
                    title={t('analytics.avgPrice')}
                    value={formatCurrency(data.prices.avg_price)}
                    subtitle={`${t('analytics.totalValue')}: ${formatCurrency(data.prices.total_value)}`}
                    color="text-green-600"
                />
                <StatCard
                    icon={EnvelopeSimple}
                    title={t('analytics.newInquiries')}
                    value={data.inquiries.new}
                    subtitle={`${data.inquiries.recent_30d} ${t('analytics.recent30d')}`}
                    color="text-purple-600"
                />
                <StatCard
                    icon={Buildings}
                    title={t('analytics.brokers')}
                    value={data.brokers.total}
                    subtitle={`${data.imports.total} ${t('analytics.imports')}`}
                    color="text-amber-600"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Property Type */}
                <Card className="border" data-testid="chart-by-type">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <ChartBar size={18} />
                            {t('analytics.byType')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {typeChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={typeChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
                                {t('common.noData')}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* By Marketing Type (Pie) */}
                <Card className="border" data-testid="chart-by-marketing">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <TrendUp size={18} />
                            {t('analytics.byMarketing')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {marketingChartData.length > 0 ? (
                            <div className="flex items-center justify-center">
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={marketingChartData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={90}
                                            dataKey="value"
                                            label={({ name, value }) => `${name}: ${value}`}
                                        >
                                            {marketingChartData.map((_, idx) => (
                                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
                                {t('common.noData')}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Inquiries Trend */}
                <Card className="border" data-testid="chart-inquiries-trend">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <EnvelopeSimple size={18} />
                            {t('analytics.inquiriesTrend')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={inquiryChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Area type="monotone" dataKey="count" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15} strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* By City */}
                <Card className="border" data-testid="chart-by-city">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <ArrowSquareOut size={18} />
                            {t('analytics.byCity')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {cityChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={cityChartData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis type="category" dataKey="city" tick={{ fontSize: 12 }} width={100} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
                                {t('common.noData')}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border" data-testid="stat-area">
                    <CardContent className="pt-5 text-center">
                        <p className="text-sm text-muted-foreground">{t('analytics.avgArea')}</p>
                        <p className="text-2xl font-bold mt-1">{data.areas.avg_area || 0} m²</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {data.areas.min_area}–{data.areas.max_area} m²
                        </p>
                    </CardContent>
                </Card>
                <Card className="border" data-testid="stat-inquiries-total">
                    <CardContent className="pt-5 text-center">
                        <p className="text-sm text-muted-foreground">{t('analytics.totalInquiries')}</p>
                        <p className="text-2xl font-bold mt-1">{data.inquiries.total}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {data.inquiries.new} {t('common.new')}
                        </p>
                    </CardContent>
                </Card>
                <Card className="border" data-testid="stat-last-import">
                    <CardContent className="pt-5 text-center">
                        <p className="text-sm text-muted-foreground">{t('analytics.lastImport')}</p>
                        <p className="text-2xl font-bold mt-1">
                            {data.imports.last ? new Date(data.imports.last.timestamp).toLocaleDateString('de-DE') : '-'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {data.imports.last ? `+${data.imports.last.created} / ~${data.imports.last.updated}` : '-'}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
